import {useThread} from "@/Store/Thread.js";
import {storeToRefs} from "pinia";
import {nextTick} from "vue";
import {route} from "ziggy-js";
import {router, usePage} from "@inertiajs/vue3";
import debounce from "lodash-es/debounce.js";
import {loadEcho} from "@/helper.js";
import {useUser} from "@/Store/User.js";

export default {
    install: (app, options) => {
        const threadStore = useThread()
        const {
            messageChannel,
            thread,
            messages,
            threads,
            dialogElement,
            hasNewMessage,
            orders
        } = storeToRefs(threadStore)

        const reload = () => {
            router.reload({
                only: ['threads', 'orders'],
                onSuccess: () => {
                    threads.value = usePage().props.threads.data
                    orders.value = usePage().props.orders.data
                }
            })
        }
        const fetchDebounced = debounce(reload, 2000)

        const threadVisibleRoutes = ['threads.*', 'admin.threads.*', 'orders.show', 'offers.show'];

        const handleNewMessage = (e, user) => {
            const isThreadPage = threadVisibleRoutes.some(pattern => route().current(pattern));
            const isDifferentThread = e.model.user_id !== user.id && (!isThreadPage || e.model.thread_id !== thread.value?.id);
            const isCurrentThread = isThreadPage && e.model.thread_id === thread.value?.id;

            if (isDifferentThread) {
                hasNewMessage.value = true
            }

            if (isCurrentThread) {
                updateCurrentThread(e.model);
            }

            updateThreadsList(e.model, user);
        };

        const updateCurrentThread = (message) => {
            messages.value[message.id] = message;

            Object.keys(messages.value).forEach(key => {
                if (key.toString().startsWith('opt_')) {
                    const optMsg = messages.value[key];
                    if (optMsg.body === message.body && optMsg.user_id === message.user_id) {
                         delete messages.value[key];
                    }
                }
            });

            if (message.offer) {
                thread.value.user.offer = message.offer;
            }
            nextTick().then(scrollToBottomSmart);
        };

        const scrollToBottomSmart = () => {
             const element = dialogElement.value;
             if (!element) return;

             const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 200;
             if (isNearBottom) {
                 element.scrollTo({
                     top: element.scrollHeight,
                     behavior: 'smooth'
                 });
             }
        };

        const updateThreadsList = (message, user) => {
             if (!threads.value || !Array.isArray(threads.value)) {
                 if (route().current('threads.*')) reload();
                 return;
             }

             const threadIndex = threads.value.findIndex(t => t.id === message.thread_id);
             if (threadIndex === -1) {
                 if (route().current('threads.*')) reload();
                 if (route().current('admin.threads.*')) fetchDebounced();
                 return;
             }

             const t = threads.value[threadIndex];
             t.updated_at = message.created_at;
             t.latest_message = message;

             const isMyMessage = message.user_id === user.id;
             const isActiveThread = message.thread_id === thread.value?.id;

             if (!isActiveThread && !isMyMessage) {
                 t.unread_messages_count = (t.unread_messages_count || 0) + 1;
             }

             threads.value.splice(threadIndex, 1);
             threads.value.unshift(t);
        };

        const updateMessageChannel = user => {
            if (typeof window === 'undefined') return;
            if (!window.Echo) {
                loadEcho(options?.reverb, () => updateMessageChannel(user));
                return;
            }

            if (!user) {
                if (messageChannel.value) {
                    messageChannel.value.stopListening('.MessageCreated');
                    messageChannel.value = null;
                }
                return;
            }

            if (messageChannel.value !== null) return;

            messageChannel.value = window.Echo.private('App.Models.User.' + user.id.toString());
            messageChannel.value.listen('.MessageCreated', (e) => handleNewMessage(e, user));
        };
        const joinChannel = () => {
            if (typeof window === 'undefined') return;
            const userStore = useUser(),
                {usersOnline} = storeToRefs(userStore)
            return window.Echo.join('user_online')
                .here((users) => {
                    usersOnline.value = users.reduce((acc, cur) => {
                        acc[cur.id] = cur;
                        return acc;
                    }, {});
                })
                .joining((user) => {
                    // user: {id: 123}
                    usersOnline.value[user.id] = user
                })
                .leaving((user) => {
                    // user: {id: 123}
                    delete usersOnline.value[user.id]
                })
        }
        const updateChannel = user => {
            if (typeof window === 'undefined') return;
            if (!window.Echo) {
                loadEcho(options?.reverb, () => updateChannel(user));
                return;
            }
            const userStore = useUser(),
                {onlineChannel} = storeToRefs(userStore)
            if (user) {
                if (onlineChannel.value == null) {
                    onlineChannel.value = joinChannel()
                }
            } else {
                if (onlineChannel.value != null) {
                    window.Echo.leave('user_online')
                    onlineChannel.value = null
                }
            }
        };
        app.provide('updateChannel', updateChannel);
        app.provide('updateMessageChannel', updateMessageChannel);
        if (typeof window === 'undefined') return;
        loadEcho(options?.reverb, () => {
            updateMessageChannel(options?.auth?.user)
            updateChannel(options?.auth?.user)
        })
    }
}
